import { useEffect } from "react";

import { DynamicEventsResponse, FunnelEvents } from "@/api/insights";
import { useWizards } from "@/wizards/framework/WizardProvider";
import { INSTALL_SDK_WIZARD_TYPE } from "@/wizards/installSdk/constants";
import { InstallSdkData } from "@/wizards/installSdk/types";
import { EventOptions, resolveEventOptions } from "./eventOptions";
import { eventsLookup, savedIds } from "./selection";
import { ConversionEventsStepProps } from "./types";

type SavedSelection = {
  eventOptions: EventOptions;
  dynamicEventsData: DynamicEventsResponse | undefined;
  eventsById: Map<string, FunnelEvents>;
  /** Picker options plus saved conversions the options leave out. */
  pickerEvents: FunnelEvents[];
  /**
   * Whether Freshpaint is known to be receiving events from the site. Null
   * when the traffic stats couldn't be loaded.
   */
  receivingEvents: boolean | null;
  retry: () => void;
};

/**
 * Derives the picker state from the wizard's event data, and seeds the
 * selection from the project's saved conversions the first time a step needs
 * it.
 */
export function useSavedSelection({
  data,
  updateData,
  context,
}: Pick<
  ConversionEventsStepProps,
  "data" | "updateData" | "context"
>): SavedSelection {
  const { enriched, library, saved } = context;
  const { state } = useWizards();
  const dynamicEventsData = saved.data;

  useEffect(() => {
    if (!dynamicEventsData || (data.bottomFunnelIds && data.midFunnelIds)) {
      return;
    }
    const ids = savedIds(dynamicEventsData);
    updateData((current) => ({
      bottomFunnelIds: current.bottomFunnelIds ?? ids.bottomFunnelIds,
      midFunnelIds: current.midFunnelIds ?? ids.midFunnelIds,
      seededBottomFunnelIds:
        current.seededBottomFunnelIds ?? ids.bottomFunnelIds,
      seededMidFunnelIds: current.seededMidFunnelIds ?? ids.midFunnelIds,
    }));
  }, [dynamicEventsData, data.bottomFunnelIds, data.midFunnelIds, updateData]);

  const eventOptions = resolveEventOptions({
    enriched,
    library,
    savedSelection: saved,
    now: Date.now(),
  });
  const options = eventOptions.status === "ready" ? eventOptions.events : [];
  const eventsById = eventsLookup(options, dynamicEventsData);
  const optionIds = new Set(options.map((event) => event.eventDefId));
  const pickerEvents = [
    ...options,
    ...[...eventsById.values()].filter(
      (event) => !optionIds.has(event.eventDefId),
    ),
  ];
  const installSeen = Object.values(state.sessions).some(
    (session) =>
      session.type === INSTALL_SDK_WIZARD_TYPE &&
      (session.data as InstallSdkData).firstEvent !== null,
  );
  const knownReceiving =
    installSeen ||
    (enriched.data ?? []).some((event) => !event.hasNoRecentTraffic);
  const receivingEvents = knownReceiving || (enriched.isError ? null : false);

  return {
    eventOptions,
    dynamicEventsData,
    eventsById,
    pickerEvents,
    receivingEvents,
    retry: () => {
      void enriched.refetch();
      void library.refetch();
      void saved.refetch();
    },
  };
}
